// src/js/components/xte-bottom-nav.js
//
// ボトムナビゲーション（全画面常駐。1024px未満は下部固定 / 1024px以上は
// 左サイドレール）の挙動強化レイヤー。IMPLEMENTATION.md 5.2 のWeb Component
// 規約に従い、PHP側（wp-content/themes/xte-theme/parts/bottom-nav.php）が
// 出力済みの軽DOM（<nav class="xte-bottom-nav"> 以下の6本の実 <a href>）を
// そのまま拡張する。Shadow DOMは使わない（currentColorの継承とSSR/JSの
// DOM統一のため。IMPLEMENTATION.md 5.2）。<a> の既定動作（実リンク）は
// 常に保たれ、JS未実行・失敗時も通常のページ遷移として機能する（20章8章）。
//
// 他ファイルとの連携契約（詳細は docs/HANDOFF.md 「ボトムナビ担当 → …」参照。
// このファイルは他ファイルを一切書き換えない）:
//   - main.js がこのファイルを副作用importすること（customElements登録のため）。
//   - タブ本体（xte-feed / xte-grid 等）が自身のスクロールコンテナへ
//     [data-xte-scroll-root][data-xte-tab-owner="{tab}"] を付与すると、
//     同一タブ再タップ（20章4.4節）が機能する。
//   - フィード（home）タブのみ、表示中に enableAutoHide(scrollRoot) を、
//     離脱時に disableAutoHide() を呼ぶと自動隠しが有効になる（20章4.8節）。
//   - core/router.js がクライアント遷移の完了時に document へ
//     `xte-router:navigated` を発火する（20章9.3節が購読側だけ定めていた契約）。
//     本コンポーネントはこのイベントと popstate でアクティブ表示を同期する。
//     以前はルーターがイベントを出さなかったため window.history.pushState /
//     replaceState をグローバルに monkey-patch してURL変化を検知していたが、
//     正式な連携経路ができたためパッチは撤去した。

import { api } from '../core/api.js';
import { getIdentity } from '../core/identity.js';
import { copy } from '../core/copy.js';

const TAB_ORDER = ['download', 'search', 'home', 'playlist', 'grid', 'bell'];

const BADGE_POLL_FOREGROUND_MS = 30000; // 前景30秒（依頼指示）
const BADGE_POLL_BACKGROUND_MS = 120000; // 背景120秒（依頼指示）
const BADGE_POLL_STOP_AFTER_HIDDEN_MS = 5 * 60 * 1000; // 5分超のバックグラウンドは停止（依頼指示）

// 20章4.8節が定める「フィード最上部付近は常に表示に固定する」しきい値。
// 対応するCSSトークン（--xte-gesture-*）が存在しない設計書固有の値のため、
// ここに定数として明記する（共通規約のトークンを新規に増やさないための判断）。
const AUTO_HIDE_TOP_GUARD_PX = 40;

/** タブごとの長押しクイックアクション（20章4.7節の表）。文言はcopy()経由で取得する。 */
const QUICK_ACTIONS = {
	download: [
		{ action: 'open_list', icon: 'clock', labelKey: 'xte_copy_nav_quick_download_open' },
		{ action: 'pause_all', icon: 'pause', labelKey: 'xte_copy_nav_quick_download_pause_all' },
	],
	search: [
		{ action: 'open_search', icon: 'search', labelKey: 'xte_copy_nav_quick_search_open' },
		{ action: 'clear_history', icon: 'close', labelKey: 'xte_copy_nav_quick_search_clear_history' },
	],
	home: [
		{ action: 'scroll_top', icon: 'arrow-up', labelKey: 'xte_copy_nav_quick_home_scroll_top' },
		{ action: 'toggle_mute', icon: 'sound-off', labelKey: 'xte_copy_nav_quick_home_mute_toggle' },
		{ action: 'refresh_recommend', icon: 'sparkle', labelKey: 'xte_copy_nav_quick_home_refresh_recommend' },
	],
	playlist: [
		{ action: 'create', icon: 'plus', labelKey: 'xte_copy_nav_quick_playlist_create' },
		{ action: 'shuffle', icon: 'shuffle', labelKey: 'xte_copy_nav_quick_playlist_shuffle' },
	],
	grid: [
		{ action: 'sort', icon: 'sort', labelKey: 'xte_copy_nav_quick_grid_sort' },
		{ action: 'density_up', icon: 'zoom-in', labelKey: 'xte_copy_nav_quick_grid_density_up' },
		{ action: 'density_down', icon: 'zoom-out', labelKey: 'xte_copy_nav_quick_grid_density_down' },
	],
	bell: [
		{ action: 'mark_all_read', icon: 'check', labelKey: 'xte_copy_nav_quick_bell_mark_all_read' },
		{ action: 'open_settings', icon: 'settings', labelKey: 'xte_copy_nav_quick_bell_settings' },
	],
};

/**
 * `--xte-gesture-*` 等、単一の真実の源であるCSSカスタムプロパティから
 * 数値を読み取る（HANDOFF.md「CSS基盤担当 → JS(core/gestures)担当」の
 * 既定パターンを踏襲）。値は単位なし（例: "500"）または px 付き
 * （例: "8px"）のどちらもありうるため parseFloat で共通に扱う。
 */
function readNumericToken(name, fallback) {
	if (typeof document === 'undefined') return fallback;
	const raw = getComputedStyle(document.documentElement).getPropertyValue(name);
	const n = parseFloat(raw);
	return Number.isFinite(n) ? n : fallback;
}

/** 100件以上を "99+" に丸める（20章5.2節）。0以下はバッジ非表示を示す null。 */
function formatBadgeCount(count) {
	if (!Number.isFinite(count) || count <= 0) return null;
	return count > 99 ? '99+' : String(count);
}

/**
 * 現在のパスがタブのパスに該当するかを判定する。
 *
 * サーバー側の `XTE_Nav::path_matches()`（wp-content/themes/xte-theme/inc/class-xte-nav.php）と
 * 同じ規則を実装する: 完全一致、または `{タブのパス}/` を境界とする前方一致のみを許可し、
 * `/videos/` と `/video/123/`、`/playlists/` と `/playlist/123/` の取り違えを防ぐ。
 * ホーム（`/`）だけは完全一致（前方一致にすると全URLに一致してしまうため）。
 *
 * 以前は完全一致だけを見ていたため、`/videos/page/2/` や `/search/?q=...` のような
 * 下位ページへクライアント遷移すると、SSRではアクティブになるタブが
 * ハイドレーション後に非アクティブへ落ちるという不整合があった。
 *
 * @param {string} currentPath 先頭・末尾のスラッシュを取り除いた現在のパス
 * @param {string} tabPath 同じく正規化したタブのパス
 */
function pathMatchesTab(currentPath, tabPath) {
	if (tabPath === '') return currentPath === '';
	return currentPath === tabPath || currentPath.startsWith(`${tabPath}/`);
}

/** URLのパス部分を `trim(path, '/')` 相当に正規化する（PHP側と揃えるため）。 */
function normalizePath(pathname) {
	return pathname.replace(/^\/+|\/+$/g, '');
}

export class XteBottomNav extends HTMLElement {
	#controller = null;
	#nav = null;
	#links = [];
	#activeTab = null;

	#pressTimer = null;
	#pressStart = null;
	#suppressNextClick = false;

	#longPressMs = 500;
	#longPressMoveTolerance = 10;
	#sameTabScrollTopThreshold = 24;
	#gapPx = 8;

	#quickMenu = null;
	#quickMenuOrigin = null;

	#autoHideTarget = null;
	#autoHideAccum = 0;
	#lastScrollTop = 0;
	#rafPending = false;

	#badgeTimer = null;
	#hiddenSince = null;

	connectedCallback() {
		this.#nav = this.querySelector('[data-xte-bottom-nav]');
		if (!this.#nav) return;

		this.#longPressMs = readNumericToken('--xte-gesture-long-press-ms', 500);
		this.#longPressMoveTolerance = readNumericToken('--xte-gesture-long-press-move', 10);
		this.#sameTabScrollTopThreshold = readNumericToken('--xte-gesture-same-tab-scroll-top', 24);
		this.#gapPx = readNumericToken('--xte-space-2', 8);

		this.#links = Array.from(this.#nav.querySelectorAll('.xte-bottom-nav__link'));
		const current = this.#links.find((link) => link.getAttribute('aria-current') === 'page');
		this.#activeTab = current ? current.dataset.xteTab ?? null : null;

		// PHP（parts/bottom-nav.php）は <nav> に data-xte-nav-index を出力しない。
		// CSS（src/css/layers/components/bottom-nav.css）はこの属性でインジケータの
		// 位置と可視性を解決するため、SSR直後の状態をここで1回だけ属性へ反映する
		// （これを怠るとインジケータが初期表示で消えたままになり、最初のタブ切替で
		//  いきなり現れるように見える）。
		this.#setIndicatorIndex(this.#activeTab);

		// ルーターがクライアント遷移時の <title> を組み立てる際に使う手掛かり。
		// 値はPHPが xte_copy() で出力した正式なタブ名（.xte-visually-hidden の
		// テキスト）であり、JS側で文言を作っているわけではない。aria-label は
		// 未読件数・無効状態が合成されるためタイトルには使えない（20章7.5節）。
		this.#links.forEach((link) => {
			const staticLabel = link.querySelector('.xte-visually-hidden')?.textContent?.trim();
			if (staticLabel && !link.dataset.xteTitle) link.dataset.xteTitle = staticLabel;
		});

		this.#controller = new AbortController();
		const { signal } = this.#controller;

		this.#links.forEach((link) => {
			link.addEventListener('pointerdown', (event) => this.#onPointerDown(event, link), { signal });
			link.addEventListener('pointerup', (event) => this.#onPointerUp(event, link), { signal });
			link.addEventListener('pointercancel', (event) => this.#onPointerUp(event, link), { signal });
			link.addEventListener('pointerleave', (event) => this.#onPointerUp(event, link), { signal });
			link.addEventListener('pointermove', (event) => this.#onPointerMove(event), { signal });
			link.addEventListener('click', (event) => this.#onClick(event, link), { signal });
			link.addEventListener('contextmenu', (event) => this.#onContextMenu(event, link), { signal });
		});

		this.#nav.addEventListener('keydown', (event) => this.#onKeydown(event), { signal });

		window.addEventListener('online', () => this.#syncOnlineState(), { signal });
		window.addEventListener('offline', () => this.#syncOnlineState(), { signal });
		this.#syncOnlineState();

		// 戻る/進む。popstate はブラウザがURLを更新した後に発火するため、
		// ルーターの再描画完了を待たずにアクティブ表示だけ先に正しくできる。
		window.addEventListener('popstate', () => this.#syncActiveFromLocation(), { signal });
		// core/router.js がクライアント遷移の完了時に発火する正式な連携経路（20章9.3節）。
		// detail.tab が渡された場合はそれを優先し、無ければ現在のURLから解決する。
		// URL→タブの対応表はサーバー側（XTE_Nav）が正であり、その規則を写しているのは
		// 本コンポーネントの #syncActiveFromLocation() なので、ルーターに
		// タブ名を決めさせない（ルーターはナビの6タブ構成を知らなくてよい）。
		document.addEventListener(
			'xte-router:navigated',
			(event) => {
				const tab = event.detail?.tab;
				if (tab) {
					this.#activateTab(tab);
				} else {
					this.#syncActiveFromLocation();
				}
			},
			{ signal }
		);

		document.addEventListener('click', (event) => this.#onDocumentClick(event), { signal });
		document.addEventListener('keydown', (event) => this.#onDocumentKeydown(event), { signal });
		window.addEventListener('scroll', () => this.#closeQuickMenu(), { signal, passive: true });
		window.addEventListener('resize', () => this.#closeQuickMenu(), { signal, passive: true });

		document.addEventListener('visibilitychange', () => this.#onVisibilityChange(), { signal });
		this.#startBadgePolling();
	}

	disconnectedCallback() {
		this.#controller?.abort();
		this.#controller = null;
		clearTimeout(this.#pressTimer);
		this.#pressTimer = null;
		this.disableAutoHide();
		this.#stopBadgePolling();
		this.#closeQuickMenu();
	}

	/* ---------- 押下フィードバック（20章4.1節） ---------- */

	#onPointerDown(event, link) {
		link.setAttribute('data-xte-pressed', 'true');
		this.#pressStart = { x: event.clientX, y: event.clientY, moved: false };
		clearTimeout(this.#pressTimer);
		this.#pressTimer = setTimeout(() => {
			this.#suppressNextClick = true;
			this.#openQuickMenu(link);
		}, this.#longPressMs);
	}

	#onPointerMove(event) {
		if (!this.#pressStart) return;
		const dx = Math.abs(event.clientX - this.#pressStart.x);
		const dy = Math.abs(event.clientY - this.#pressStart.y);
		if (dx > this.#longPressMoveTolerance || dy > this.#longPressMoveTolerance) {
			this.#pressStart.moved = true;
			clearTimeout(this.#pressTimer);
		}
	}

	#onPointerUp(event, link) {
		link.removeAttribute('data-xte-pressed');
		clearTimeout(this.#pressTimer);
		this.#pressStart = null;
	}

	/* ---------- クリック（遷移 / 同一タブ再タップ） ---------- */

	#onClick(event, link) {
		if (this.#suppressNextClick) {
			// 直前の長押しでクイックメニューを開いたばかりのため、
			// 続けて発火する click は無視する（誤ナビゲーション防止）。
			this.#suppressNextClick = false;
			event.preventDefault();
			return;
		}

		if (link.getAttribute('aria-disabled') === 'true') {
			event.preventDefault();
			return;
		}

		const isModified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
		if (isModified) return; // 新規タブ等、ブラウザ既定動作に委ねる（20章8.1節）

		const tab = link.dataset.xteTab;

		if (tab === this.#activeTab) {
			// 同一タブ再タップ: 実遷移は常に止める（同一ページ内操作のため。20章4.4節）。
			event.preventDefault();
			this.#handleSameTabRetap(tab);
			return;
		}

		// 別タブへの遷移: <a> の既定動作は妨げない。実際のSPA遷移・
		// View Transitions・フォールバックは document へのクリックリスナーで
		// 捕捉する router.js（他担当所有。IMPLEMENTATION.md 3章）に委ねる。
		// ここでは体感速度のため、遷移の成否を待たず見た目のみ即時に
		// 切り替える（実URLへの実リンクである以上、最悪でもフルリロードで
		// 正しい状態に復帰するため壊れない）。
		this.#activateTab(tab);
		this.#vibrate(8);
	}

	#handleSameTabRetap(tab) {
		const root = document.querySelector(`[data-xte-scroll-root][data-xte-tab-owner="${tab}"]`);
		if (!root) return; // 対応するタブ本体が未実装/未接続でも壊れない（進行的強化）
		if (root.scrollTop > this.#sameTabScrollTopThreshold) {
			root.scrollTo({ top: 0, behavior: 'smooth' });
		} else {
			root.dispatchEvent(new CustomEvent('xte-nav:refresh', { bubbles: true, detail: { tab } }));
		}
	}

	/* ---------- アクティブ状態の反映 ---------- */

	#activateTab(tab) {
		if (this.#activeTab === tab) return;
		this.#activeTab = tab;

		this.#links.forEach((link) => {
			const isCurrent = link.dataset.xteTab === tab;
			if (isCurrent) {
				link.setAttribute('aria-current', 'page');
			} else {
				link.removeAttribute('aria-current');
			}
			this.#syncIconVariant(link, isCurrent);
		});

		this.#setIndicatorIndex(tab);
		this.toggleFeedContext(tab === 'home');
	}

	/**
	 * アクティブ時は fill 版アイコンへ差し替える（20章4.3節）。
	 * PHP側の xte_render_icon() が出力するのは `<svg><use href="#xte-icon-{name}"></use></svg>`
	 * という素のSVGであり `<xte-icon>` 要素ではないため、切替は `<use href>` の
	 * 付け替えで行う（`variant` 属性は存在しない）。
	 *
	 * サーバー側の xte_resolve_icon_symbol_id() は fill版が無い名前を outline へ
	 * 静かにフォールバックさせる。JS側も同じ挙動にするため、スプライトに
	 * symbol が実在するかを core/icon.js と同じ方法で確認してから差し替える
	 * （現時点で6タブ全てに fill 版が存在するが、将来アイコンが差し替わっても
	 *  存在しないsymbolを指してアイコンが消える事故を起こさないため）。
	 */
	#syncIconVariant(link, isCurrent) {
		const use = link.querySelector('.xte-bottom-nav__icon use');
		const iconName = link.dataset.xteIcon;
		if (!use || !iconName) return;
		const fillId = `xte-icon-${iconName}-fill`;
		const useFill = isCurrent && document.getElementById(fillId) !== null;
		use.setAttribute('href', useFill ? `#${fillId}` : `#xte-icon-${iconName}`);
	}

	/** インライン style は使わず、<nav data-xte-nav-index> の書き換えのみで位置を伝える（CSS側で解決）。 */
	#setIndicatorIndex(tab) {
		if (!this.#nav) return;
		const index = TAB_ORDER.indexOf(tab);
		if (index === -1) {
			this.#nav.removeAttribute('data-xte-nav-index');
		} else {
			this.#nav.setAttribute('data-xte-nav-index', String(index));
		}
	}

	toggleFeedContext(isFeed) {
		this.#nav?.classList.toggle('xte-bottom-nav--on-feed', isFeed);
	}

	/**
	 * popstate ／ `xte-router:navigated` から呼ぶ。現在のURLに対応する <a> を
	 * サーバー側（XTE_Nav::current_tab()）と同じ規則で探し、アクティブ化する。
	 * どのタブにも該当しないURL（`/video/123/`・`/tag/xxx/` 等）では null を渡し、
	 * SSR時と同様に「どのタブもアクティブでない」状態にする。
	 */
	#syncActiveFromLocation() {
		if (typeof window === 'undefined') return;
		const currentPath = normalizePath(window.location.pathname);
		const match = this.#links.find((link) => {
			try {
				const tabPath = normalizePath(new URL(link.href, window.location.origin).pathname);
				return pathMatchesTab(currentPath, tabPath);
			} catch {
				return false;
			}
		});
		this.#activateTab(match ? match.dataset.xteTab ?? null : null);
	}

	/* ---------- キーボード操作（20章7.2節。追加的なフォーカス移動のみ） ---------- */

	#onKeydown(event) {
		const idx = this.#links.findIndex((link) => link === document.activeElement);
		if (idx === -1) return;

		const isRail = window.matchMedia('(min-width: 1024px)').matches;
		const nextKey = isRail ? 'ArrowDown' : 'ArrowRight';
		const prevKey = isRail ? 'ArrowUp' : 'ArrowLeft';

		if (event.key === nextKey && idx < this.#links.length - 1) {
			event.preventDefault();
			this.#links[idx + 1].focus();
		} else if (event.key === prevKey && idx > 0) {
			event.preventDefault();
			this.#links[idx - 1].focus();
		} else if (event.key === 'Home') {
			event.preventDefault();
			this.#links[0].focus();
		} else if (event.key === 'End') {
			event.preventDefault();
			this.#links[this.#links.length - 1].focus();
		}
	}

	/* ---------- 長押しクイックメニュー（20章4.7節） ---------- */

	#onContextMenu(event, link) {
		event.preventDefault();
		this.#openQuickMenu(link);
	}

	#openQuickMenu(link) {
		if (this.#pressStart && this.#pressStart.moved) return;
		if (link.getAttribute('aria-disabled') === 'true') return;
		if (!this.#nav) return;

		this.#closeQuickMenu();

		const tab = link.dataset.xteTab;
		const actions = QUICK_ACTIONS[tab];
		if (!actions || actions.length === 0) return;

		this.#vibrate(15);

		const menu = document.createElement('div');
		menu.className = 'xte-bottom-nav__quick-menu';
		menu.setAttribute('role', 'menu');
		const originLabel = link.querySelector('.xte-visually-hidden');
		menu.setAttribute('aria-label', originLabel ? originLabel.textContent ?? '' : '');

		actions.forEach((entry) => {
			const button = document.createElement('button');
			button.type = 'button';
			button.className = 'xte-bottom-nav__quick-action';
			button.setAttribute('role', 'menuitem');

			const icon = document.createElement('xte-icon');
			icon.setAttribute('name', entry.icon);
			icon.setAttribute('size', 'sm');
			icon.setAttribute('aria-hidden', 'true');

			const label = document.createElement('span');
			label.textContent = copy(entry.labelKey);

			button.append(icon, label);
			button.addEventListener('click', () => {
				document.dispatchEvent(
					new CustomEvent('xte-nav:quick-action', { detail: { tab, action: entry.action } })
				);
				this.#closeQuickMenu();
			});

			menu.appendChild(button);
		});

		// <button> は <a> の子孫にできない（HTML仕様上、インタラクティブ
		// コンテンツの入れ子は不可）ため、起点 <a> の内側ではなく
		// position: fixed の <nav> へ直接メニューを追加する。表示位置は
		// getBoundingClientRect() から算出し、カスタムプロパティ経由で渡す
		// （IMPLEMENTATION.md 6.3の許容パターン。静的なstyle属性は書かない）。
		this.#nav.appendChild(menu);
		this.#positionQuickMenu(menu, link);

		this.#quickMenu = menu;
		this.#quickMenuOrigin = link;

		const firstButton = menu.querySelector('button');
		firstButton?.focus();
	}

	#positionQuickMenu(menu, link) {
		if (!this.#nav) return;
		const linkRect = link.getBoundingClientRect();
		const navRect = this.#nav.getBoundingClientRect();
		const isRail = window.matchMedia('(min-width: 1024px)').matches;

		menu.style.removeProperty('--xte-quick-menu-inset-inline-start');
		menu.style.removeProperty('--xte-quick-menu-inset-block-start');
		menu.style.removeProperty('--xte-quick-menu-inset-block-end');

		if (isRail) {
			menu.style.setProperty('--xte-quick-menu-inset-block-start', `${linkRect.top - navRect.top}px`);
			menu.style.setProperty('--xte-quick-menu-inset-inline-start', `${linkRect.right - navRect.left + this.#gapPx}px`);
		} else {
			menu.style.setProperty(
				'--xte-quick-menu-inset-inline-start',
				`${linkRect.left - navRect.left + linkRect.width / 2}px`
			);
			menu.style.setProperty('--xte-quick-menu-inset-block-end', `${navRect.bottom - linkRect.top + this.#gapPx}px`);
		}
	}

	#closeQuickMenu() {
		if (!this.#quickMenu) return;
		this.#quickMenu.remove();
		this.#quickMenu = null;
		this.#quickMenuOrigin?.focus();
		this.#quickMenuOrigin = null;
	}

	#onDocumentClick(event) {
		if (!this.#quickMenu) return;
		if (this.#quickMenu.contains(event.target) || event.target === this.#quickMenuOrigin) return;
		this.#closeQuickMenu();
	}

	#onDocumentKeydown(event) {
		if (this.#quickMenu && event.key === 'Escape') {
			event.preventDefault();
			this.#closeQuickMenu();
		}
	}

	/* ---------- 自動隠し／再表示（フィードのみ。20章4.8節。公開API） ---------- */

	#onScroll = () => {
		if (this.#rafPending) return;
		this.#rafPending = true;
		requestAnimationFrame(() => {
			this.#rafPending = false;
			const root = this.#autoHideTarget;
			if (!root || !this.#nav) return;

			const top = root.scrollTop;
			const delta = top - this.#lastScrollTop;
			if ((delta > 0 && this.#autoHideAccum < 0) || (delta < 0 && this.#autoHideAccum > 0)) {
				this.#autoHideAccum = 0;
			}
			this.#autoHideAccum += delta;
			this.#lastScrollTop = top;

			if (top < AUTO_HIDE_TOP_GUARD_PX) {
				this.#nav.removeAttribute('data-xte-hidden');
			} else if (this.#autoHideAccum > this.#sameTabScrollTopThreshold) {
				this.#nav.setAttribute('data-xte-hidden', 'true');
			} else if (this.#autoHideAccum < -this.#sameTabScrollTopThreshold) {
				this.#nav.removeAttribute('data-xte-hidden');
			}
		});
	};

	enableAutoHide(scrollRoot) {
		this.disableAutoHide();
		if (!scrollRoot) return;
		this.#autoHideTarget = scrollRoot;
		this.#autoHideAccum = 0;
		this.#lastScrollTop = scrollRoot.scrollTop;
		scrollRoot.addEventListener('scroll', this.#onScroll, { passive: true });
	}

	disableAutoHide() {
		if (this.#autoHideTarget) {
			this.#autoHideTarget.removeEventListener('scroll', this.#onScroll);
		}
		this.#autoHideTarget = null;
		this.#nav?.removeAttribute('data-xte-hidden');
	}

	/* ---------- 未読バッジ（20章5章）。ポーリング: 前景30秒/背景120秒/5分超で停止 ---------- */

	#startBadgePolling() {
		this.#pollBadgeOnce();
		this.#scheduleNextBadgePoll();
	}

	#stopBadgePolling() {
		clearTimeout(this.#badgeTimer);
		this.#badgeTimer = null;
	}

	#scheduleNextBadgePoll() {
		clearTimeout(this.#badgeTimer);
		const interval = document.hidden ? BADGE_POLL_BACKGROUND_MS : BADGE_POLL_FOREGROUND_MS;
		this.#badgeTimer = setTimeout(() => {
			this.#pollBadgeOnce();
			this.#scheduleNextBadgePoll();
		}, interval);
	}

	async #pollBadgeOnce() {
		if (document.hidden && this.#hiddenSince && Date.now() - this.#hiddenSince > BADGE_POLL_STOP_AFTER_HIDDEN_MS) {
			return; // 5分超のバックグラウンド放置は停止し、復帰時（visibilitychange）に再開する
		}

		const token = getIdentity();
		if (!token) return; // 識別子未発行のゲストは通知を持たないため取得自体をしない

		try {
			const data = await api.get('/notifications/unread-count');
			if (data && typeof data.unread_count === 'number') {
				this.setBadge('bell', data.unread_count);
			}
		} catch (err) {
			// ポーリング失敗でUIを壊さない。次周期で自然に再試行する
			// （docs/HANDOFF.md「ボトムナビ担当 → API/identity担当」参照:
			// GET経由の認証ヘッダ付与が未整備のため、現状は失敗しうる既知の制約）。
			console.warn('[xte-bottom-nav] 未読件数の取得に失敗しました。', err);
		}
	}

	#onVisibilityChange() {
		if (document.hidden) {
			this.#hiddenSince = Date.now();
		} else {
			this.#hiddenSince = null;
			this.#pollBadgeOnce();
		}
		this.#scheduleNextBadgePoll();
	}

	/** 外部（通知コンポーネント等）からも呼べる公開API。 */
	setBadge(tab, count) {
		if (!this.#nav) return;
		const badge = this.#nav.querySelector(`[data-xte-badge="${tab}"]`);
		if (!badge) return;
		const countEl = badge.querySelector('.xte-bottom-nav__badge-count');

		const prev = Number(badge.dataset.xteCount || 0);
		const next = Number.isFinite(count) ? Math.max(0, Math.trunc(count)) : 0;
		badge.dataset.xteCount = String(next);

		const text = formatBadgeCount(next);
		if (!text) {
			badge.hidden = true;
		} else {
			if (countEl) countEl.textContent = text;
			badge.hidden = false;
		}

		if (next > prev) {
			badge.setAttribute('data-xte-pop', 'true');
			badge.addEventListener('animationend', () => badge.removeAttribute('data-xte-pop'), { once: true });
		}

		this.#updateAriaLabel(tab, next);
	}

	#updateAriaLabel(tab, count) {
		const link = this.#links.find((el) => el.dataset.xteTab === tab);
		if (!link) return;
		const staticLabelEl = link.querySelector('.xte-visually-hidden');
		const staticLabel = staticLabelEl ? staticLabelEl.textContent ?? '' : '';
		const disabled = link.getAttribute('aria-disabled') === 'true';

		let label = staticLabel;
		if (count > 0) {
			label += `、${count > 99 ? copy('xte_copy_nav_unread_99plus') : copy('xte_copy_nav_unread_count', { count })}`;
		}
		if (disabled) {
			label += `、${copy('xte_copy_nav_disabled_suffix')}`;
		}
		link.setAttribute('aria-label', label);
	}

	/* ---------- オフライン状態（20章6章の状態表） ---------- */

	#syncOnlineState() {
		const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
		this.#nav?.classList.toggle('xte-bottom-nav--offline', offline);
	}

	/* ---------- ハプティック（20章4.1節・4.7節: 粗ポインタのみ） ---------- */

	#vibrate(ms) {
		if (typeof window === 'undefined' || !window.matchMedia('(pointer: coarse)').matches) return;
		if (typeof navigator.vibrate === 'function') navigator.vibrate(ms);
	}
}

if (!customElements.get('xte-bottom-nav')) {
	customElements.define('xte-bottom-nav', XteBottomNav);
}
